"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  type TransformCtx,
  TransformResults,
} from "@/components/tools/linear-algebra/transform-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type AngleUnit,
  parseAngle,
} from "@/lib/tool-kit/linear-algebra/angle-input";
import { piMultiple } from "@/lib/tool-kit/linear-algebra/exact-angle";
import type {
  Point,
  ReflectionLine,
  Step,
} from "@/lib/tool-kit/linear-algebra/transform";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  formatStyled,
  type NumberStyle,
  parseRational,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const GEOMETRIC_TRANSFORMATION_CALCULATOR_STRINGS = {
  pointsLabel: "Points or polygon vertices — one x, y pair per line (up to 12)",
  pointsHint:
    "Three or more points are drawn as a polygon, in the order entered.",
  stepsHeading: "Transformations, applied in order",
  stepN: "Step {n}",
  stepTypeLabel: "Transformation",
  types: {
    translate: "Translate (slide)",
    reflect: "Reflect (flip)",
    rotate: "Rotate (turn)",
    dilate: "Dilate (resize)",
  },
  addStep: "Add a transformation",
  removeStep: "Remove step {n}",
  unitLabel: "Angle unit",
  units: { degrees: "Degrees", radians: "Radians" },
  dx: "Move right by dx",
  dy: "Move up by dy",
  lineLabel: "Reflect across",
  lines: {
    "x-axis": "the x-axis (y = 0)",
    "y-axis": "the y-axis (x = 0)",
    origin: "the origin (a point reflection)",
    "y=x": "the line y = x",
    "y=-x": "the line y = −x",
    slope: "the line y = mx + b",
    vertical: "the vertical line x = c",
  },
  slopeM: "Slope m",
  interceptB: "y-intercept b",
  verticalC: "x = c",
  angle: "Angle of rotation",
  angleHintDegrees: "Degrees, such as 90 or 45.",
  angleHintRadians:
    "Radians: a number such as 1.5708, or a multiple of π such as pi/2.",
  directionLabel: "Direction",
  directions: { ccw: "Counterclockwise", cw: "Clockwise" },
  centerX: "Center x",
  centerY: "Center y",
  scaleFactor: "Scale factor k",
  scaleHint:
    "Greater than 1 enlarges, between 0 and 1 shrinks, negative also flips through the center.",
  entryHint: "Whole numbers, decimals or fractions such as 3/4.",
  results: "Results",
  enterPoints: "Enter at least one point, one x, y pair per line.",
  badPoint:
    "Line {n} is not an x, y pair of numbers. Write each point as 3, 4 or 3 4.",
  tooManyPoints: "Use at most 12 points.",
  badStep: "Step {n}: {reason}",
  stepErrors: {
    number: "enter every value as a number or a fraction.",
    angle: "enter the angle as a number (in radians you can also write pi/2).",
  },
  describe: {
    translate: "Translate by ({dx}, {dy})",
    reflect: "Reflect across {line}",
    rotateCcw: "Rotate {angle} counterclockwise about ({cx}, {cy})",
    rotateCw: "Rotate {angle} clockwise about ({cx}, {cy})",
    dilate: "Dilate by scale factor {k} about ({cx}, {cy})",
    slopeLine: "the line y = {m}x + {b}",
    verticalLine: "the line x = {c}",
  },
  degreesUnit: "{value}°",
  radiansUnit: "{value} rad",
  pointText: "({x}, {y})",
  distanceScaleLabel: "Distances are multiplied by",
  determinantLabel: "Area factor (determinant)",
  orientationLabel: "Orientation",
  orientationPreserved: "Preserved",
  orientationReversed: "Reversed (mirror image)",
  areaLabel: "Polygon area",
  areaChange: "{before} → {after}",
  imagesHeading: "Images of the points",
  colPoint: "Point",
  colOriginal: "Original",
  colImage: "Image",
  copied: "Copied!",
  copyImages: "Copy images",
  matrixHeading: "Transformation matrix",
  matrixIntro:
    "Each step is a 3 × 3 matrix acting on (x, y, 1). The matrices multiply right to left, so the first step is the rightmost factor.",
  compositeLabel: "Combined matrix",
  stepMatrices: "Matrix of each step",
  stepMatrixLabel: "Matrix of step {n}",
  decimalNote:
    "Entries are decimal approximations: an angle that is not a multiple of 30° or 45°, or steps whose square roots differ (such as 30° then 45°), have no single exact form.",
  plotHeading: "Before and after",
  plotTitle:
    "The original shape in blue and its image in orange drawn to scale on a coordinate plane, with the reflection lines and centers of rotation or dilation in green",
  plotKey:
    "Blue is the original, orange the final image, dotted outlines the shape after each earlier step, and green marks a reflection line or a center.",
  downloadPng: "Download diagram (PNG)",
} as const;

export type TransformStrings =
  typeof GEOMETRIC_TRANSFORMATION_CALCULATOR_STRINGS;

type StepType = keyof TransformStrings["types"];
type LineId = keyof TransformStrings["lines"];
type Direction = keyof TransformStrings["directions"];

interface StepState {
  id: number;
  type: StepType;
  dx: string;
  dy: string;
  line: LineId;
  m: string;
  b: string;
  c: string;
  angle: string;
  direction: Direction;
  cx: string;
  cy: string;
  k: string;
  kx: string;
  ky: string;
}

const MAX_POINTS = 12;
const MAX_STEPS = 4;
const TYPE_ORDER: StepType[] = ["translate", "reflect", "rotate", "dilate"];
const LINE_ORDER: LineId[] = [
  "x-axis",
  "y-axis",
  "origin",
  "y=x",
  "y=-x",
  "slope",
  "vertical",
];
const PAIR_SEPARATORS = /[\s;]+/;
const COMMA_SPACE = /,\s+/g;
const BRACKETS = /[()[\]]/g;
const DECIMAL_INPUT = /[.,]/;
const DEFAULT_POINTS = "1, 1\n4, 1\n1, 3";

function newStep(id: number, overrides: Partial<StepState> = {}): StepState {
  return {
    id,
    type: "rotate",
    dx: "3",
    dy: "-2",
    line: "y-axis",
    m: "1",
    b: "0",
    c: "2",
    angle: "90",
    direction: "ccw",
    cx: "0",
    cy: "0",
    k: "2",
    kx: "0",
    ky: "0",
    ...overrides,
  };
}

function parsePoints(
  text: string
): Point[] | { badLine: number } | "empty" | "many" {
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  if (lines.length === 0) {
    return "empty";
  }
  if (lines.length > MAX_POINTS) {
    return "many";
  }
  const points: Point[] = [];
  for (const [index, line] of lines.entries()) {
    const cleaned = line
      .replace(BRACKETS, " ")
      .replace(COMMA_SPACE, " ")
      .trim();
    let parts = cleaned.split(PAIR_SEPARATORS);
    if (parts.length === 1 && cleaned.includes(",")) {
      parts = cleaned.split(",");
    }
    const [x, y] = parts.map((part) => parseRational(part));
    if (parts.length !== 2 || !x || !y) {
      return { badLine: index + 1 };
    }
    points.push({ x, y });
  }
  return points;
}

type StepResult = { step: Step } | { error: "number" | "angle" };

function parseStep(s: StepState, unit: AngleUnit): StepResult {
  const rationals = (...texts: string[]) => {
    const parsed = texts.map(parseRational);
    return parsed.some((x) => x === null) ? null : (parsed as Rational[]);
  };
  if (s.type === "translate") {
    const v = rationals(s.dx, s.dy);
    return v
      ? { step: { type: "translate", dx: v[0], dy: v[1] } }
      : { error: "number" };
  }
  if (s.type === "reflect") {
    return parseReflection(s);
  }
  if (s.type === "dilate") {
    const v = rationals(s.k, s.kx, s.ky);
    return v
      ? { step: { type: "dilate", k: v[0], center: { x: v[1], y: v[2] } } }
      : { error: "number" };
  }
  const center = rationals(s.cx, s.cy);
  const angle = parseAngle(s.angle, unit);
  if (angle === null) {
    return { error: "angle" };
  }
  if (center === null) {
    return { error: "number" };
  }
  const sign = s.direction === "cw" ? -1 : 1;
  return {
    step: {
      type: "rotate",
      degrees: sign * angle.degrees,
      exactDegrees:
        angle.exactDegrees === null ? null : sign * angle.exactDegrees,
      center: { x: center[0], y: center[1] },
    },
  };
}

function parseReflection(s: StepState): StepResult {
  if (s.line === "slope") {
    const m = parseRational(s.m);
    const b = parseRational(s.b);
    return m && b
      ? { step: { type: "reflect", line: { kind: "slope", m, b } } }
      : { error: "number" };
  }
  if (s.line === "vertical") {
    const c = parseRational(s.c);
    return c
      ? { step: { type: "reflect", line: { kind: "vertical", c } } }
      : { error: "number" };
  }
  const line: ReflectionLine = { kind: s.line };
  return { step: { type: "reflect", line } };
}

function describeStep(step: Step, ctx: TransformCtx, unit: AngleUnit): string {
  const { strings } = ctx;
  const v = (x: Rational) => formatStyled(x, ctx.style, ctx.mark);
  const d = strings.describe;
  switch (step.type) {
    case "translate":
      return t(d.translate, { dx: v(step.dx), dy: v(step.dy) });
    case "reflect": {
      let line: string = strings.lines[step.line.kind];
      if (step.line.kind === "slope") {
        line = t(d.slopeLine, { m: v(step.line.m), b: v(step.line.b) });
      } else if (step.line.kind === "vertical") {
        line = t(d.verticalLine, { c: v(step.line.c) });
      }
      return t(d.reflect, { line });
    }
    case "rotate": {
      const magnitude = Math.abs(step.degrees);
      const exact =
        step.exactDegrees === null ? null : Math.abs(step.exactDegrees);
      let angle = t(strings.degreesUnit, {
        value: ctx.fmt(magnitude, { maximumFractionDigits: 4 }),
      });
      if (unit === "radians") {
        angle = t(strings.radiansUnit, {
          value:
            exact === null
              ? ctx.fmt((magnitude * Math.PI) / 180, {
                  maximumFractionDigits: 6,
                })
              : piMultiple(exact),
        });
      }
      return t(step.degrees < 0 ? d.rotateCw : d.rotateCcw, {
        angle,
        cx: v(step.center.x),
        cy: v(step.center.y),
      });
    }
    default:
      return t(d.dilate, {
        k: v(step.k),
        cx: v(step.center.x),
        cy: v(step.center.y),
      });
  }
}

function Outcome({
  pointsText,
  steps,
  unit,
  ctx,
}: {
  pointsText: string;
  steps: StepState[];
  unit: AngleUnit;
  ctx: TransformCtx;
}) {
  const { strings } = ctx;
  const points = parsePoints(pointsText);
  if (points === "empty") {
    return <Notice>{strings.enterPoints}</Notice>;
  }
  if (points === "many") {
    return <ErrorNotice>{strings.tooManyPoints}</ErrorNotice>;
  }
  if (!Array.isArray(points)) {
    return (
      <ErrorNotice>
        {t(strings.badPoint, { n: ctx.fmt(points.badLine) })}
      </ErrorNotice>
    );
  }
  const parsed: Step[] = [];
  for (const [index, state] of steps.entries()) {
    const result = parseStep(state, unit);
    if ("error" in result) {
      return (
        <ErrorNotice>
          {t(strings.badStep, {
            n: ctx.fmt(index + 1),
            reason: strings.stepErrors[result.error],
          })}
        </ErrorNotice>
      );
    }
    parsed.push(result.step);
  }
  return (
    <TransformResults
      ctx={ctx}
      points={points}
      steps={parsed}
      stepTexts={parsed.map((step) => describeStep(step, ctx, unit))}
    />
  );
}

function StepEditor({
  state,
  index,
  count,
  unit,
  strings,
  onChange,
  onRemove,
  fmt,
}: {
  state: StepState;
  index: number;
  count: number;
  unit: AngleUnit;
  strings: TransformStrings;
  onChange: (patch: Partial<StepState>) => void;
  onRemove: () => void;
  fmt: (n: number) => string;
}) {
  const id = (name: string) => `gt-${state.id}-${name}`;
  const field = (name: keyof StepState, label: string, hint?: string) => (
    <TextField
      hint={hint}
      id={id(name)}
      label={label}
      mono
      numeric
      onChange={(value) => onChange({ [name]: value })}
      value={String(state[name])}
    />
  );
  return (
    <fieldset className={`${CARD} space-y-4`}>
      <legend className="px-1 font-medium text-foreground text-sm">
        {t(strings.stepN, { n: fmt(index + 1) })}
      </legend>
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-48 flex-1">
          <SelectField
            id={id("type")}
            label={strings.stepTypeLabel}
            onChange={(type) => onChange({ type })}
            options={TYPE_ORDER.map((value) => ({
              value,
              label: strings.types[value],
            }))}
            value={state.type}
          />
        </div>
        {count > 1 ? (
          <button
            aria-label={t(strings.removeStep, { n: fmt(index + 1) })}
            className={BUTTON_SMALL}
            onClick={onRemove}
            type="button"
          >
            ✕
          </button>
        ) : null}
      </div>
      {state.type === "translate" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {field("dx", strings.dx)}
          {field("dy", strings.dy)}
        </div>
      ) : null}
      {state.type === "reflect" ? (
        <div className="space-y-4">
          <SelectField
            id={id("line")}
            label={strings.lineLabel}
            onChange={(line) => onChange({ line })}
            options={LINE_ORDER.map((value) => ({
              value,
              label: strings.lines[value],
            }))}
            value={state.line}
          />
          {state.line === "slope" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {field("m", strings.slopeM)}
              {field("b", strings.interceptB)}
            </div>
          ) : null}
          {state.line === "vertical" ? field("c", strings.verticalC) : null}
        </div>
      ) : null}
      {state.type === "rotate" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {field(
            "angle",
            strings.angle,
            unit === "degrees"
              ? strings.angleHintDegrees
              : strings.angleHintRadians
          )}
          <PillGroup
            label={strings.directionLabel}
            name={id("direction")}
            onChange={(direction) => onChange({ direction })}
            options={(["ccw", "cw"] as const).map((value) => ({
              value,
              label: strings.directions[value],
            }))}
            value={state.direction}
          />
          {field("cx", strings.centerX)}
          {field("cy", strings.centerY)}
        </div>
      ) : null}
      {state.type === "dilate" ? (
        <div className="grid gap-4 sm:grid-cols-3">
          {field("k", strings.scaleFactor, strings.scaleHint)}
          {field("kx", strings.centerX)}
          {field("ky", strings.centerY)}
        </div>
      ) : null}
    </fieldset>
  );
}

export default function GeometricTransformationCalculatorWidget() {
  const strings = useToolUi(GEOMETRIC_TRANSFORMATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [pointsText, setPointsText] = useState(DEFAULT_POINTS);
  const [unit, setUnit] = useState<AngleUnit>("degrees");
  const [steps, setSteps] = useState<StepState[]>([newStep(1)]);
  const [nextId, setNextId] = useState(2);
  const patch = (id: number, changes: Partial<StepState>) =>
    setSteps((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...changes } : s))
    );
  const numeric = steps.flatMap((s) => [
    s.dx,
    s.dy,
    s.m,
    s.b,
    s.c,
    s.cx,
    s.cy,
    s.k,
    s.kx,
    s.ky,
  ]);
  const style: NumberStyle =
    pointsText.includes(".") || numeric.some((text) => DECIMAL_INPUT.test(text))
      ? "decimal"
      : "fraction";
  const ctx: TransformCtx = { strings, style, mark: decimalMarkOf(fmt), fmt };
  return (
    <>
      <TextAreaField
        hint={strings.pointsHint}
        id="gt-points"
        label={strings.pointsLabel}
        onChange={setPointsText}
        rows={4}
        value={pointsText}
      />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.stepsHeading}
          </h2>
          <PillGroup
            label={strings.unitLabel}
            name="gt-unit"
            onChange={setUnit}
            options={(["degrees", "radians"] as const).map((id) => ({
              value: id,
              label: strings.units[id],
            }))}
            value={unit}
          />
        </div>
        {steps.map((state, index) => (
          <StepEditor
            count={steps.length}
            fmt={fmt}
            index={index}
            key={state.id}
            onChange={(changes) => patch(state.id, changes)}
            onRemove={() =>
              setSteps((prev) => prev.filter((s) => s.id !== state.id))
            }
            state={state}
            strings={strings}
            unit={unit}
          />
        ))}
        {steps.length < MAX_STEPS ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setSteps((prev) => [
                ...prev,
                newStep(nextId, { type: "translate" }),
              ]);
              setNextId((n) => n + 1);
            }}
            type="button"
          >
            {strings.addStep}
          </button>
        ) : null}
      </div>
      <p className="text-muted-foreground text-xs">{strings.entryHint}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome ctx={ctx} pointsText={pointsText} steps={steps} unit={unit} />
      </section>
    </>
  );
}
